import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, Globe, Linkedin, MapPin, Users, Briefcase,
  Plus, RefreshCw, ShieldCheck, FileText, ExternalLink,
  Phone, Mail, Star, AlertTriangle,
} from 'lucide-react';
import api from '../api';
import { Company, Establishment, Contact, Deal, Activity, Note, PipelineStage } from '../types';
import ScoreBadge from '../components/ScoreBadge';
import Modal from '../components/Modal';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR');

const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [enriching, setEnriching] = useState(false);
  const [enrichMessage, setEnrichMessage] = useState('');

  // Sub-data
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [subLoading, setSubLoading] = useState(false);

  // Modals
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  // Contact form
  const [contactForm, setContactForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', isDecisionMaker: false,
  });
  const [contactFormError, setContactFormError] = useState('');
  const [contactFormLoading, setContactFormLoading] = useState(false);

  // Deal form
  const [dealForm, setDealForm] = useState({
    title: '', estimatedAmount: '', machineType: '', probability: '50', estimatedCloseDate: '', nextStepDescription: '', nextStepDueDate: '', stageId: '',
  });
  const [dealFormError, setDealFormError] = useState('');
  const [dealFormLoading, setDealFormLoading] = useState(false);
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);

  // Activity form
  const [activityForm, setActivityForm] = useState({
    type: 'call' as string, subject: '', description: '', activityDate: new Date().toISOString().split('T')[0],
  });
  const [activityFormError, setActivityFormError] = useState('');
  const [activityFormLoading, setActivityFormLoading] = useState(false);

  const fetchCompany = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/companies/${id}`);
      setCompany(res.data);
    } catch (err) {
      setError('Entreprise introuvable.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCompany();
    // Fetch pipeline stages for deal creation form
    api.get('/deals/stages').then(res => {
      const stages = res.data.data || res.data || [];
      setPipelineStages(stages);
    }).catch(() => {});
  }, [fetchCompany]);

  useEffect(() => {
    if (!id) return;
    setSubLoading(true);
    const fetchSubData = async () => {
      try {
        if (activeTab === 'establishments') {
          const res = await api.get(`/companies/${id}/establishments`);
          setEstablishments(res.data.data || res.data || []);
        } else if (activeTab === 'contacts') {
          const res = await api.get(`/contacts`, { params: { companyId: id, limit: 100 } });
          setContacts(res.data.data || res.data || []);
        } else if (activeTab === 'deals') {
          const res = await api.get(`/deals`, { params: { companyId: id, limit: 100 } });
          setDeals(res.data.data || res.data || []);
        } else if (activeTab === 'activities') {
          const res = await api.get(`/activities`, { params: { companyId: id, limit: 100 } });
          setActivities(res.data.data || res.data || []);
        } else if (activeTab === 'notes') {
          const res = await api.get(`/companies/${id}/notes`);
          setNotes(res.data.data || res.data || []);
        }
      } catch (err) {
        // silently fail for sub-data
      } finally {
        setSubLoading(false);
      }
    };
    fetchSubData();
  }, [id, activeTab]);

  const handleEnrich = async () => {
    if (!company) return;
    setEnriching(true);
    setEnrichMessage('');
    try {
      await api.post(`/pappers/enrich/${company.siren}`);
      setEnrichMessage('Enrichissement effectué avec succès.');
      fetchCompany();
    } catch (err: any) {
      setEnrichMessage(err.response?.data?.message || 'Erreur lors de l\'enrichissement.');
    } finally {
      setEnriching(false);
    }
  };

  const handleAddContact = async () => {
    setContactFormError('');
    if (!contactForm.firstName.trim() || !contactForm.lastName.trim()) {
      setContactFormError('Le prénom et le nom sont obligatoires.');
      return;
    }
    setContactFormLoading(true);
    try {
      await api.post('/contacts', { ...contactForm, companyId: id });
      setShowContactModal(false);
      setContactForm({ firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', isDecisionMaker: false });
      const res = await api.get('/contacts', { params: { companyId: id, limit: 100 } });
      setContacts(res.data.data || res.data || []);
    } catch (err: any) {
      setContactFormError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setContactFormLoading(false);
    }
  };

  const handleAddDeal = async () => {
    setDealFormError('');
    if (!dealForm.title.trim()) {
      setDealFormError('Le titre est obligatoire.');
      return;
    }
    if (!dealForm.stageId) {
      setDealFormError('Veuillez sélectionner une étape du pipeline.');
      return;
    }
    setDealFormLoading(true);
    try {
      const payload: any = {
        title: dealForm.title,
        companyId: id,
        stageId: dealForm.stageId,
        probability: Number(dealForm.probability),
      };
      if (dealForm.estimatedAmount) payload.estimatedAmount = Number(dealForm.estimatedAmount);
      if (dealForm.machineType) payload.machineType = dealForm.machineType;
      if (dealForm.estimatedCloseDate) payload.estimatedCloseDate = dealForm.estimatedCloseDate;
      if (dealForm.nextStepDescription) payload.nextStepDescription = dealForm.nextStepDescription;
      if (dealForm.nextStepDueDate) payload.nextStepDueDate = dealForm.nextStepDueDate;

      const res = await api.post('/deals', payload);
      setShowDealModal(false);
      setDealForm({ title: '', estimatedAmount: '', machineType: '', probability: '50', estimatedCloseDate: '', nextStepDescription: '', nextStepDueDate: '', stageId: '' });
      navigate(`/deals/${res.data.id}`);
    } catch (err: any) {
      setDealFormError(err.response?.data?.message || JSON.stringify(err.response?.data) || 'Erreur lors de la création.');
    } finally {
      setDealFormLoading(false);
    }
  };

  const handleAddActivity = async () => {
    setActivityFormError('');
    if (!activityForm.subject.trim()) {
      setActivityFormError('Le sujet est obligatoire.');
      return;
    }
    setActivityFormLoading(true);
    try {
      await api.post('/activities', { ...activityForm, companyId: id });
      setShowActivityModal(false);
      setActivityForm({ type: 'call', subject: '', description: '', activityDate: new Date().toISOString().split('T')[0] });
      const res = await api.get('/activities', { params: { companyId: id, limit: 100 } });
      setActivities(res.data.data || res.data || []);
    } catch (err: any) {
      setActivityFormError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setActivityFormLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteLoading(true);
    try {
      await api.post(`/companies/${id}/notes`, { content: newNote });
      setNewNote('');
      const res = await api.get(`/companies/${id}/notes`);
      setNotes(res.data.data || res.data || []);
    } catch (err) {
      // ignore
    } finally {
      setNoteLoading(false);
    }
  };

  const handleOptOutToggle = async () => {
    if (!company) return;
    try {
      await api.patch(`/companies/${id}`, { optOut: !company.optOut });
      setCompany({ ...company, optOut: !company.optOut });
    } catch (err) {
      // ignore
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  if (error || !company) {
    return (
      <div className="empty-state">
        <AlertTriangle size={48} />
        <p className="empty-state-title">{error || 'Entreprise introuvable'}</p>
        <button className="btn" onClick={() => navigate('/companies')}>Retour à la liste</button>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Vue d\'ensemble' },
    { key: 'establishments', label: 'Établissements' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'deals', label: 'Affaires' },
    { key: 'activities', label: 'Activités' },
    { key: 'notes', label: 'Notes' },
    { key: 'compliance', label: 'Conformité' },
  ];

  return (
    <div>
      <div className="detail-header">
        <div>
          <h1>
            <Building2 size={28} />
            {company.name}
            <ScoreBadge score={company.score} />
            <span className={`badge ${company.isActive ? 'badge-success' : 'badge-danger'}`}>
              {company.isActive ? 'Active' : 'Inactive'}
            </span>
          </h1>
          {company.tradeName && <div className="text-secondary mt-1">{company.tradeName}</div>}
        </div>
        <div className="btn-group">
          <button className="btn" onClick={handleEnrich} disabled={enriching}>
            <RefreshCw size={16} className={enriching ? 'spin' : ''} />
            {enriching ? 'Enrichissement...' : 'Enrichir (Pappers)'}
          </button>
        </div>
      </div>

      {enrichMessage && (
        <div className={`mb-3 ${enrichMessage.includes('succès') ? 'badge-success' : 'badge-danger'}`} style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '0.9rem' }}>
          {enrichMessage}
        </div>
      )}

      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="two-col">
          <div className="card">
            <div className="card-header"><h3>Informations générales</h3></div>
            <div className="card-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">SIREN</div>
                  <div className="detail-item-value">{company.siren}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Forme juridique</div>
                  <div className="detail-item-value">{company.legalForm || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Code NAF</div>
                  <div className="detail-item-value">{company.nafCode || '-'} {company.nafLabel && `(${company.nafLabel})`}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Date de création</div>
                  <div className="detail-item-value">{company.creationDate ? formatDate(company.creationDate) : '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Capital</div>
                  <div className="detail-item-value">{company.capital ? formatCurrency(company.capital) : '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Effectif</div>
                  <div className="detail-item-value">
                    {company.workforceRange || '-'}
                    {company.workforceCount !== undefined && company.workforceCount !== null && ` (${company.workforceCount})`}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Multi-site</div>
                  <div className="detail-item-value">{company.isMultiSite ? 'Oui' : 'Non'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Source données</div>
                  <div className="detail-item-value">{company.dataSource || '-'}</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="card mb-3">
              <div className="card-header"><h3>Adresse</h3></div>
              <div className="card-body">
                <div className="inline-flex mb-2">
                  <MapPin size={16} />
                  <span>
                    {company.addressStreet && <>{company.addressStreet}<br /></>}
                    {company.addressPostalCode} {company.addressCity}
                    {company.addressCountry && `, ${company.addressCountry}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header"><h3>Liens</h3></div>
              <div className="card-body">
                {company.website && (
                  <div className="inline-flex mb-2">
                    <Globe size={16} />
                    <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer">
                      {company.website} <ExternalLink size={12} />
                    </a>
                  </div>
                )}
                {company.linkedinUrl && (
                  <div className="inline-flex mb-2">
                    <Linkedin size={16} />
                    <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      LinkedIn <ExternalLink size={12} />
                    </a>
                  </div>
                )}
                {!company.website && !company.linkedinUrl && (
                  <p className="text-secondary">Aucun lien disponible</p>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Score</h3></div>
              <div className="card-body">
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <ScoreBadge score={company.score} />
                  <div className="text-secondary mt-1">{company.score}/100</div>
                </div>
                {company.scoreDetails && typeof company.scoreDetails === 'object' && (
                  <div style={{ fontSize: '0.85rem' }}>
                    {Object.entries(company.scoreDetails).map(([key, value]) => (
                      <div key={key} className="info-row">
                        <span className="info-label">{key}</span>
                        <span className="info-value">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Establishments Tab */}
      {activeTab === 'establishments' && (
        <div className="card">
          <div className="card-body">
            {subLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : establishments.length === 0 ? (
              <div className="empty-state">
                <Building2 size={48} />
                <p className="empty-state-title">Aucun établissement</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>SIRET</th>
                      <th>Nom</th>
                      <th>Adresse</th>
                      <th>Effectif</th>
                      <th>Type</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {establishments.map((e) => (
                      <tr key={e.id}>
                        <td>{e.siret}</td>
                        <td>{e.name}</td>
                        <td>{[e.addressStreet, e.addressPostalCode, e.addressCity].filter(Boolean).join(', ') || '-'}</td>
                        <td>{e.workforceRange || '-'}</td>
                        <td>
                          {e.isHeadquarters ? (
                            <span className="badge badge-info">Siège</span>
                          ) : (
                            <span className="badge badge-gray">Secondaire</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${e.isActive ? 'badge-success' : 'badge-danger'}`}>
                            {e.isActive ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="card">
          <div className="card-header">
            <h3>Contacts</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowContactModal(true)}>
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <div className="card-body">
            {subLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : contacts.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p className="empty-state-title">Aucun contact</p>
                <button className="btn btn-primary" onClick={() => setShowContactModal(true)}>
                  <Plus size={16} /> Ajouter un contact
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="table table-clickable">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Poste</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Décideur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}>
                        <td className="font-semibold">{c.firstName} {c.lastName}</td>
                        <td>{c.jobTitle || '-'}</td>
                        <td>
                          {c.email ? (
                            <span className="inline-flex"><Mail size={14} /> {c.email}</span>
                          ) : '-'}
                        </td>
                        <td>
                          {(c.phone || c.mobile) ? (
                            <span className="inline-flex"><Phone size={14} /> {c.phone || c.mobile}</span>
                          ) : '-'}
                        </td>
                        <td>
                          {c.isDecisionMaker && <span className="badge badge-purple"><Star size={12} /> Décideur</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deals Tab */}
      {activeTab === 'deals' && (
        <div className="card">
          <div className="card-header">
            <h3>Affaires</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowDealModal(true)}>
              <Plus size={14} /> Créer une affaire
            </button>
          </div>
          <div className="card-body">
            {subLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : deals.length === 0 ? (
              <div className="empty-state">
                <Briefcase size={48} />
                <p className="empty-state-title">Aucune affaire</p>
                <button className="btn btn-primary" onClick={() => setShowDealModal(true)}>
                  <Plus size={16} /> Créer une affaire
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="table table-clickable">
                  <thead>
                    <tr>
                      <th>Titre</th>
                      <th>Étape</th>
                      <th>Montant</th>
                      <th>Probabilité</th>
                      <th>Date de clôture prévue</th>
                      <th>Résultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((d) => (
                      <tr key={d.id} onClick={() => navigate(`/deals/${d.id}`)}>
                        <td className="font-semibold">{d.title}</td>
                        <td>
                          {d.stage && (
                            <span className="badge" style={{ background: d.stage.color + '20', color: d.stage.color }}>
                              {d.stage.name}
                            </span>
                          )}
                        </td>
                        <td>{d.estimatedAmount !== undefined && d.estimatedAmount !== null ? formatCurrency(d.estimatedAmount) : '-'}</td>
                        <td>{d.probability}%</td>
                        <td>{d.estimatedCloseDate ? formatDate(d.estimatedCloseDate) : '-'}</td>
                        <td>
                          {d.outcome === 'won' && <span className="badge badge-success">Gagnée</span>}
                          {d.outcome === 'lost' && <span className="badge badge-danger">Perdue</span>}
                          {!d.outcome && <span className="badge badge-info">En cours</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="card">
          <div className="card-header">
            <h3>Activités</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <div className="card-body">
            {subLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : activities.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <p className="empty-state-title">Aucune activité</p>
              </div>
            ) : (
              <div className="timeline">
                {activities.map((a) => (
                  <div key={a.id} className="timeline-item">
                    <div className="timeline-item-header">
                      <span className="timeline-item-title">{a.subject}</span>
                      <span className="badge badge-info">{a.type}</span>
                    </div>
                    <div className="timeline-item-date">
                      {formatDate(a.activityDate)}
                      {a.user && ` - ${a.user.firstName} ${a.user.lastName}`}
                    </div>
                    {a.description && <div className="timeline-item-body">{a.description}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="card">
          <div className="card-header"><h3>Notes</h3></div>
          <div className="card-body">
            <div className="form-group">
              <textarea
                className="form-textarea"
                placeholder="Ajouter une note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
              />
              <button
                className="btn btn-primary btn-sm mt-2"
                onClick={handleAddNote}
                disabled={noteLoading || !newNote.trim()}
              >
                {noteLoading ? <span className="spinner spinner-sm" /> : 'Ajouter'}
              </button>
            </div>

            {subLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : notes.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <p className="empty-state-title">Aucune note</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                {notes.map((n) => (
                  <div key={n.id} className="card" style={{ padding: '12px 16px' }}>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{n.content}</div>
                    <div className="text-secondary mt-1" style={{ fontSize: '0.8rem' }}>
                      {n.user && `${n.user.firstName} ${n.user.lastName} - `}
                      {formatDate(n.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="card">
          <div className="card-header"><h3><ShieldCheck size={18} /> Conformité RGPD</h3></div>
          <div className="card-body">
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-item-label">Opt-out</div>
                <div className="detail-item-value">
                  <label className="form-check">
                    <input
                      type="checkbox"
                      checked={company.optOut}
                      onChange={handleOptOutToggle}
                    />
                    <span>{company.optOut ? 'Opt-out activé (ne pas contacter)' : 'Pas d\'opt-out'}</span>
                  </label>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Source des données</div>
                <div className="detail-item-value">{company.dataSource || 'Non renseigné'}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Date de collecte</div>
                <div className="detail-item-value">{formatDate(company.createdAt)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Conservation</div>
                <div className="detail-item-value">
                  Les données sont conservées conformément à la politique de rétention. Dernière mise à jour : {formatDate(company.createdAt)}.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Ajouter un contact"
        footer={
          <>
            <button className="btn" onClick={() => setShowContactModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddContact} disabled={contactFormLoading}>
              {contactFormLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {contactFormError && <div className="login-error">{contactFormError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Prénom *</label>
            <input className="form-input" value={contactForm.firstName} onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="form-input" value={contactForm.lastName} onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input type="email" className="form-input" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Téléphone</label>
            <input className="form-input" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile</label>
            <input className="form-input" value={contactForm.mobile} onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Poste</label>
          <input className="form-input" value={contactForm.jobTitle} onChange={(e) => setContactForm({ ...contactForm, jobTitle: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-check">
            <input type="checkbox" checked={contactForm.isDecisionMaker} onChange={(e) => setContactForm({ ...contactForm, isDecisionMaker: e.target.checked })} />
            Décideur
          </label>
        </div>
      </Modal>

      {/* Deal Modal */}
      <Modal
        isOpen={showDealModal}
        onClose={() => setShowDealModal(false)}
        title="Créer une affaire"
        footer={
          <>
            <button className="btn" onClick={() => setShowDealModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddDeal} disabled={dealFormLoading}>
              {dealFormLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {dealFormError && <div className="login-error">{dealFormError}</div>}
        <div className="form-group">
          <label className="form-label">Titre *</label>
          <input className="form-input" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} placeholder="Ex: Vente machine CNC" />
        </div>
        <div className="form-group">
          <label className="form-label">Étape du pipeline *</label>
          <select className="form-select" value={dealForm.stageId} onChange={(e) => setDealForm({ ...dealForm, stageId: e.target.value })}>
            <option value="">Sélectionner une étape...</option>
            {pipelineStages.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Montant estimé</label>
            <input type="number" className="form-input" value={dealForm.estimatedAmount} onChange={(e) => setDealForm({ ...dealForm, estimatedAmount: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Probabilité (%)</label>
            <input type="number" className="form-input" min={0} max={100} value={dealForm.probability} onChange={(e) => setDealForm({ ...dealForm, probability: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Type de machine</label>
          <input className="form-input" value={dealForm.machineType} onChange={(e) => setDealForm({ ...dealForm, machineType: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Date de clôture prévue</label>
          <input type="date" className="form-input" value={dealForm.estimatedCloseDate} onChange={(e) => setDealForm({ ...dealForm, estimatedCloseDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Prochaine étape</label>
          <input className="form-input" value={dealForm.nextStepDescription} onChange={(e) => setDealForm({ ...dealForm, nextStepDescription: e.target.value })} placeholder="Ex: Appeler le client" />
        </div>
        <div className="form-group">
          <label className="form-label">Date prochaine étape</label>
          <input type="date" className="form-input" value={dealForm.nextStepDueDate} onChange={(e) => setDealForm({ ...dealForm, nextStepDueDate: e.target.value })} />
        </div>
      </Modal>

      {/* Activity Modal */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Ajouter une activité"
        footer={
          <>
            <button className="btn" onClick={() => setShowActivityModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddActivity} disabled={activityFormLoading}>
              {activityFormLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {activityFormError && <div className="login-error">{activityFormError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select className="form-select" value={activityForm.type} onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}>
              <option value="call">Appel</option>
              <option value="email">Email</option>
              <option value="meeting">Réunion</option>
              <option value="letter">Courrier</option>
              <option value="note">Note</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={activityForm.activityDate} onChange={(e) => setActivityForm({ ...activityForm, activityDate: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Sujet *</label>
          <input className="form-input" value={activityForm.subject} onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} rows={4} />
        </div>
      </Modal>
    </div>
  );
};

export default CompanyDetailPage;
